import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import Translation from '@swf/components/Translation/Translation';
import { RoutingKey } from '@swf/core/enums';
import type { RootState } from '@swf/core/store';
import type { HGPokerGameTile } from '@swf/core/types/Poker';
import { TileType } from '@swf/core/types/Poker';
import {
  GameTileSection,
  Loader,
  NoData,
  PokerGameTileContainer,
  useFocusGamesList,
  useHomeGamesWidget,
  useMapGameData,
  usePollLobbyData
} from '@swf/poker';

import {
  TXTCLI_Registered_In_Tournaments,
  TXTWCL_No_Tournaments_to_Show,
  TXTWCL_Tournaments_from_your_clubs_wi_ELL
} from './Tournaments.messages.json';
import styles from './Tournaments.scss';
import NoDataIcon from './assets/no-data.svg';

const Tournaments: React.FC = () => {
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();
  const clubs = useSelector(widgetSelect.clubs);
  const { registeredTournaments: loading } = useSelector(widgetSelect.loading);
  const { mappedTournaments } = useMapGameData({
    shouldMapTournaments: true
  });
  const scheduledTournamentTiles = useSelector((rootState: RootState) =>
    widgetSelect.getScheduledTournTiles(rootState, mappedTournaments)
  );
  const registeredTournamentTiles = useSelector((rootState: RootState) =>
    widgetSelect.getRegisteredTournTiles(rootState, mappedTournaments)
  );

  const allRegisteredTournamentTiles = useMemo(
    () =>
      Object.keys(registeredTournamentTiles).reduce((acc, clubId) => {
        if (isEmpty(registeredTournamentTiles)) return [];

        acc.push(...registeredTournamentTiles[clubId]);
        return acc;
      }, [] as HGPokerGameTile[]),
    [registeredTournamentTiles]
  );

  const noClubs = isEmpty(clubs);
  const noGames = isEmpty(mappedTournaments);
  const noRegistered = isEmpty(allRegisteredTournamentTiles);

  usePollLobbyData();
  useFocusGamesList();

  useEffect(() => {
    widgetDispatch.fetchRegisteredTournaments();
  }, [widgetDispatch]);

  if (!loading && noClubs) {
    return <Redirect routingKey={RoutingKey.HomeGamesClubs} />;
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.loading]: loading
      })}
    >
      <Loader loading={loading}>
        {noGames ? (
          <NoData
            icon={<NoDataIcon />}
            primaryText={<Translation {...TXTWCL_No_Tournaments_to_Show} />}
            secondaryText={<Translation {...TXTWCL_Tournaments_from_your_clubs_wi_ELL} />}
          />
        ) : (
          <>
            {!noRegistered && (
              <GameTileSection
                games={allRegisteredTournamentTiles}
                title={<Translation {...TXTCLI_Registered_In_Tournaments} />}
                type={TileType.Registered}
              />
            )}
            <PokerGameTileContainer clubs={clubs} games={scheduledTournamentTiles} />
          </>
        )}
      </Loader>
    </div>
  );
};

export default Tournaments;
